import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Badge } from "antd";
import styled from "styled-components";
import BellIcon from "Assets/Images/bell.svg";
import NotificationBar from "Components/CommonComponents/NotificationsBar/NotificationBar";
import { UserInfoContext } from "Context/CommonContext";
import useMasterContext from "Context/MasterContext";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
import {
  BASE_URL,
  USER_NOTIFICATIONS,
} from "utilFunctions/Constants/urlConstants";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import useResponsive from "Context/useResponsive";

const StyledBadge = styled(Badge)`
  .ant-badge-dot.ant-scroll-number {
    left: 12px;
  }
`;

const iconBoxStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 38,
  height: 38,
  borderRadius: 10,
  background: "rgba(255,255,255,0.18)",
  backdropFilter: "blur(6px)",
  cursor: "pointer",
  border: "none",
  outline: "none",
  transition: "background 0.18s",
};

interface UnreadCommentNotificationItem {
  batchId: number;
  subject: string;
  unreadCount: number;
}

const BellIconButton: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const userInfo = React.useContext<any>(UserInfoContext);
  const { masters } = useMasterContext();
  const { unreadComments } = masters || {};
  const [showNotification, setShowNotification] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadPostComments, setUnreadPostComments] = useState<any[]>([]);
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [limit] = React.useState<number>(10);
  const [offset, setOffSet] = React.useState<number>(0);
  const notificationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setUnreadPostComments(Array.isArray(unreadComments) ? unreadComments : []);
  }, [unreadComments]);

  useEffect(() => {
    const handleUnreadCommentRemoved = (event: any) => {
      const { batchId, subject } = event.detail || {};
      if (!batchId || !subject) return;
      setUnreadPostComments((previous) =>
        previous
          .map((batch: any) => {
            if (batch?.batchId !== batchId) return batch;
            return {
              ...batch,
              subjects: Array.isArray(batch?.subjects)
                ? batch.subjects.filter(
                    (item: any) => item?.subject !== subject
                  )
                : [],
            };
          })
          .filter(
            (batch: any) =>
              Array.isArray(batch?.subjects) && batch.subjects.length > 0
          )
      );
    };

    window.addEventListener(
      "unread-post-comment-removed",
      handleUnreadCommentRemoved
    );

    return () => {
      window.removeEventListener(
        "unread-post-comment-removed",
        handleUnreadCommentRemoved
      );
    };
  }, []);

  const unreadCommentNotifications: UnreadCommentNotificationItem[] =
    useMemo(() => {
      if (!Array.isArray(unreadPostComments)) return [];
      return unreadPostComments.flatMap((batch: any) => {
        const subjects = Array.isArray(batch?.subjects) ? batch.subjects : [];
        return subjects
          .filter(
            (subject: any) =>
              subject?.subject &&
              Array.isArray(subject?.postIds) &&
              subject.postIds.length > 0
          )
          .map((subject: any) => ({
            batchId: batch?.batchId,
            subject: subject?.subject,
            unreadCount: subject?.postIds?.length || 0,
          }));
      });
    }, [unreadPostComments]);

  const unreadCommentCount = useMemo(() => {
    return unreadCommentNotifications.reduce(
      (total, notification) => total + (notification?.unreadCount || 0),
      0
    );
  }, [unreadCommentNotifications]);

  const onFetchError = (error: any) => {
    console.log("error onFetchError", error);
    setHasMore(false);
    if (offset !== 0) {
      setOffSet(0);
    }
  };

  const { makeRequest: GetNotifications, loading } = useGetApi(
    (searchData: any) => {
      if (
        searchData &&
        searchData != null &&
        searchData["data"] &&
        searchData["data"].length
      ) {
        setNotifications((previous: any) => [
          ...previous,
          ...searchData["data"],
        ]);
        setHasMore(
          searchData["data"] && searchData["data"].length > 0 ? true : false
        );
      } else {
        setNotifications([]);
        setHasMore(false);
      }
    },
    (error: any) => {
      onFetchError(error);
    }
  );

  React.useEffect(() => {
    const url =
      BASE_URL +
      USER_NOTIFICATIONS +
      "?facultyId=" +
      userInfo.employeeId +
      "&limit=" +
      limit +
      "&offset=" +
      0;
    if (userInfo.employeeId) {
      if (notifications.length > 0) {
        setNotifications(notifications);
      } else {
        GetNotifications(url);
      }
    }
  }, [location, userInfo.employeeId]);

  const nextHandler = () => {
    const newOffset = offset + limit;
    setOffSet((previous) => previous + limit);
    const url =
      BASE_URL +
      USER_NOTIFICATIONS +
      "?facultyId=" +
      userInfo.employeeId +
      "&limit=" +
      limit +
      "&offset=" +
      newOffset;
    GetNotifications(url);
  };

  const handleShowNotification = () => {
    pushToDataLayerForGTM({
      event: "ag_notifications_view_notification_center",
      USER_ROLE: userInfo?.fexRole,
      COUNT: unreadCommentCount,
    });

    if (isMobile) {
      navigate("/admin/notifications", {
        state: {
          unreadPostComments: unreadCommentNotifications,
        },
      });
      return;
    }
    setNotifications([]);
    setShowNotification((prev) => !prev);
    setTimeout(() => {
      const url =
        BASE_URL +
        USER_NOTIFICATIONS +
        "?facultyId=" +
        userInfo.employeeId +
        "&limit=" +
        limit +
        "&offset=" +
        0;
      GetNotifications(url);
    }, 10);
  };

  const handleUnreadPostCommentClick = (batchId: number, subject: string) => {
    setUnreadPostComments((previous) =>
      previous
        .map((batch: any) => {
          if (batch?.batchId !== batchId) return batch;

          const updatedSubjects = Array.isArray(batch?.subjects)
            ? batch.subjects.filter((item: any) => item?.subject !== subject)
            : [];

          return {
            ...batch,
            subjects: updatedSubjects,
          };
        })
        .filter(
          (batch: any) =>
            Array.isArray(batch?.subjects) && batch.subjects.length > 0
        )
    );

    setShowNotification(false);

    navigate(`/admin/mybatch/${batchId}`, {
      state: {
        selectedSubject: subject,
      },
    });
  };

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotification(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div onClick={handleShowNotification} style={{ cursor: "pointer" }}>
        <StyledBadge
          count={notifications.length + unreadCommentCount}
          size="small"
          overflowCount={99}
          style={{
            outline: "none",
            userSelect: "none",
            zIndex: "1001",
            marginTop: "4px",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          <Button
            style={iconBoxStyle}
            icon={<img src={BellIcon} alt="Notifications" />}
          />
        </StyledBadge>
      </div>

      {showNotification && (
        <div className="notfication-bar" ref={notificationRef}>
          <NotificationBar
            handleAfterRoute={() => {
              setShowNotification(false);
              setNotifications([]);
            }}
            hasMore={hasMore}
            nextHandler={nextHandler}
            notifications={notifications}
            loading={loading}
            setShowNotification={setShowNotification}
            unreadPostComments={unreadCommentNotifications}
            unreadPostCommentsLoading={false}
            onUnreadPostCommentClick={handleUnreadPostCommentClick}
          />
        </div>
      )}
    </>
  );
};

export default BellIconButton;
